/**
 * AgentApiClient - Frontend HTTP client for AI agent endpoints
 *
 * Calls builder-service API for all AI operations.
 * Supports multi-provider: pass `provider` and `model` fields.
 *
 * Endpoints:
 *   POST /api/agent/update-block     — regenerate block content
 *   POST /api/agent/create-strategy  — create strategy with AI (replaces webhook)
 */

import { getAuth } from 'firebase/auth';
import { emitBillingBlocked, parseBillingBlockedPayload } from '@modules/billing-access';
import { agentApiRefusal } from '@modules/shared-types';
import type {
  AgentApiErrorResponse,
  AgentUpdateBlockRequest,
  AgentUpdateBlockResponse,
  AgentCreateStrategyRequest,
  AgentCreateStrategyResponse,
  AgentGenerateStrategyRequest,
  AgentGenerateStrategyResponse,
  AgentStrategyContractRepairRequest,
  AgentPersistStrategyFromTaskRequest,
  AgentPersistStrategyFromTaskResponse,
  AgentContinueStrategyGenerationRequest,
  AgentContinueStrategyGenerationResponse,
  AgentStrategyContractRepairResponse,
  AgentGenerateInsightsRequest,
  AgentGenerateInsightsResponse,
  AgentResumeInsightsRequest,
  AgentResumeInsightsResponse,
  AgentGenerateInsightVariablesRequest,
  AgentGenerateInsightVariablesResponse,
  AgentScrapeAnalyzeRequest,
  AgentScrapeAnalyzeResponse,
  AgentApplyStagedRequest,
  AgentApplyStagedResponse,
  AgentGenerateAudiencesRequest,
  AgentGenerateAudiencesResponse,
  AgentGenerateThemeV2FromImageRequest,
  AgentGenerateThemeV2FromImageResponse,
  AgentAnalyzeScreenReproductionRequest,
  AgentAnalyzeScreenReproductionResponse,
  AgentGenerateScreenReproductionRequest,
  AgentGenerateScreenReproductionResponse,
  CliResponseEnvelope,
  CliBlockReviewData,
  CliStrategyRepairData,
  CliStrategyBlockAssetSlotData,
  CliStrategyBlockAssetSlotGenerateData,
  CliStrategyBlockAssetSlotGenerateRequest,
  CliStrategyBlockAssetSlotListData,
  CliStrategyBlockAssetSlotMutationData,
  CliStrategyBlockAssetSlotMutationRequest,
  CliStrategyBlockAssetSlotPromptRegenerateData,
  CliStrategyBlockAssetSlotPromptRegenerateRequest,
  CliStrategyBlockPullData,
  CliStrategyBlockPullRequest,
  CliStrategyCompileData,
  CliStrategyCompileRequest,
  CliStrategyLiftData,
  CliStrategyLiftRequest,
  CliStrategyPushData,
  CliStrategyPushRequest,
} from '@modules/shared-types';

export interface AgentApiClientOptions {
  baseUrl?: string;
}

type AgentCliResponse<T> = CliResponseEnvelope<T> | {
  success: false;
  error: string;
};

export class AgentApiClient {
  private readonly baseUrl: string;
  private abortController: AbortController | null = null;

  constructor(options: AgentApiClientOptions = {}) {
    this.baseUrl = options.baseUrl ||
      import.meta.env.VITE_AGENT_API_URL ||
      import.meta.env.VITE_BUILDER_API_URL ||
      'http://localhost:8080';
  }

  /**
   * Generate updated block content via AI.
   * Fire-and-forget: server saves result to Firestore,
   * frontend listens via real-time subscription.
   */
  async updateBlock(request: AgentUpdateBlockRequest): Promise<AgentUpdateBlockResponse> {
    return this.post<AgentUpdateBlockResponse>('/api/agent/update-block', request);
  }

  /**
   * Create full strategy with AI-generated blocks.
   * Replaces the old webhook-based flow.
   */
  async createStrategy(request: AgentCreateStrategyRequest): Promise<AgentCreateStrategyResponse> {
    return this.post<AgentCreateStrategyResponse>('/api/agent/create-strategy', request);
  }

  /**
   * Legacy compatibility method used by StrategyOrchestrator.
   * Maps the old (input, compositePrompt) signature to the new updateBlock() API.
   */
  async generateBlockUpdate(
    input: {
      mode?: string;
      projectId: string;
      strategyId: string;
      prompt?: string;
      userId: string;
      sourceData: Record<string, unknown>;
      currentBlock?: { blockId: string; blockType: string; content: Record<string, unknown> };
      model?: string;
      maxTokens?: number;
    },
    _compositePrompt?: unknown
  ): Promise<{ success: boolean; data?: unknown; error?: string; usage?: unknown }> {
    const response = await this.updateBlock({
      projectId: input.projectId,
      strategyId: input.strategyId,
      blockId: input.currentBlock?.blockId || '',
      blockType: input.currentBlock?.blockType || 'custom',
      prompt: input.prompt || '',
      sourceData: input.sourceData as any,
      userId: input.userId,
      currentBlock: input.currentBlock?.content,
      model: input.model,
      maxTokens: input.maxTokens,
    });

    return {
      success: response.success,
      data: (response as any).result,
      error: response.error,
      usage: (response as any).usage,
    };
  }

  /**
   * Generate a complete strategy with multi-phase AI pipeline.
   * Fire-and-forget: returns taskId immediately, backend processes in background.
   * Frontend tracks progress via Firestore real-time listener on task document.
   */
  async generateStrategy(request: AgentGenerateStrategyRequest): Promise<AgentGenerateStrategyResponse> {
    return this.post<AgentGenerateStrategyResponse>('/api/agent/generate-strategy', request);
  }

  /**
   * G4a — save a finished generation run from its own per-block state (no
   * provider call, no second bill). `mode: 'apply'` rebuilds, refuses with named
   * reasons (writing nothing) or saves, in ONE request.
   */
  async persistStrategyFromTask(
    request: AgentPersistStrategyFromTaskRequest,
  ): Promise<AgentPersistStrategyFromTaskResponse> {
    return this.post<AgentPersistStrategyFromTaskResponse>('/api/agent/strategy/persist-from-task', request);
  }

  /**
   * G4b — «Generate again» on an unfinished run: continue it (only the blocks it
   * never finished) or fall back to a full generation from that run's own
   * parameters. The answer says which happened (`mode` + `reason`).
   */
  async continueStrategyGeneration(
    request: AgentContinueStrategyGenerationRequest,
  ): Promise<AgentContinueStrategyGenerationResponse> {
    return this.post<AgentContinueStrategyGenerationResponse>('/api/agent/generate-strategy', request);
  }

  async repairStrategyContract(
    request: AgentStrategyContractRepairRequest,
  ): Promise<AgentStrategyContractRepairResponse> {
    return this.post<AgentStrategyContractRepairResponse>('/api/agent/strategy/repair-contract', request);
  }

  async reviewStrategyBlock(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    instructions?: string;
    provider?: string;
    model?: string;
  }): Promise<AgentCliResponse<CliBlockReviewData>> {
    const { projectId, strategyId, blockId, ...body } = request;
    return this.post(
      `/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/blocks/${encodeURIComponent(blockId)}/review`,
      body,
    );
  }

  async repairStrategy(request: {
    projectId: string;
    strategyId: string;
    blockId?: string;
    screenId?: string;
    apply?: boolean;
    approvedPlanChecksum?: `sha256:${string}`;
    perBlockRetries?: number;
    resetAttemptLedger?: boolean;
    provider?: string;
    model?: string;
  }): Promise<AgentCliResponse<CliStrategyRepairData>> {
    const { projectId, strategyId, ...body } = request;
    return this.post(
      `/api/cli/v1/projects/${encodeURIComponent(projectId)}/strategies/${encodeURIComponent(strategyId)}/repair`,
      body,
    );
  }

  async compileStrategyToOnboarding(
    request: CliStrategyCompileRequest & { projectId: string; strategyId: string; userId?: string },
  ): Promise<{ success: boolean; error?: string; code?: string; field?: string } & Partial<CliStrategyCompileData>> {
    return this.post('/api/agent/strategy/compile-onboarding', request);
  }

  async liftOnboardingToStrategy(
    request: CliStrategyLiftRequest & { projectId: string; funnelId: string; versionId: string; userId?: string },
  ): Promise<{ success: boolean; error?: string } & Partial<CliStrategyLiftData>> {
    return this.post('/api/agent/strategy/lift-onboarding', request);
  }

  async pullStrategyBlock(
    request: CliStrategyBlockPullRequest & { projectId: string; strategyId: string; blockId: string; userId?: string },
  ): Promise<{ success: boolean; error?: string; code?: string } & Partial<CliStrategyBlockPullData>> {
    return this.post('/api/agent/strategy/pull-block', request);
  }

  async pushStrategyToOnboarding(
    request: CliStrategyPushRequest & { projectId: string; strategyId: string; userId?: string },
  ): Promise<{ success: boolean; error?: string; code?: string } & Partial<CliStrategyPushData>> {
    return this.post('/api/agent/strategy/push-onboarding', request);
  }

  async listStrategyBlockAssetSlots(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotListData>> {
    return this.get(this.strategyBlockAssetSlotPath(request));
  }

  async getStrategyBlockAssetSlot(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    slotKey: string;
    screenId: string;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotData>> {
    return this.get(this.strategyBlockAssetSlotPath(request, request.slotKey, { screenId: request.screenId }));
  }

  async mutateStrategyBlockAssetSlot(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    slotKey: string;
    screenId: string;
    mutation: Omit<CliStrategyBlockAssetSlotMutationRequest, 'screenId'>;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotMutationData>> {
    return this.patch(
      this.strategyBlockAssetSlotPath(request, request.slotKey, { screenId: request.screenId }),
      {
        ...request.mutation,
        screenId: request.screenId,
      },
    );
  }

  async selectStrategyBlockAssetSlot(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    slotKey: string;
    screenId: string;
    experimentId: string;
    dryRun?: boolean;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotMutationData>> {
    return this.mutateStrategyBlockAssetSlot({
      ...request,
      mutation: {
        selectedExperimentId: request.experimentId,
        dryRun: request.dryRun,
      },
    });
  }

  async regenerateStrategyBlockAssetSlotPrompt(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    slotKey: string;
    screenId: string;
    regeneration?: Omit<CliStrategyBlockAssetSlotPromptRegenerateRequest, 'screenId'>;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotPromptRegenerateData>> {
    return this.post(
      this.strategyBlockAssetSlotPath(request, request.slotKey, { screenId: request.screenId }, 'regenerate-prompt'),
      {
        ...(request.regeneration ?? {}),
        screenId: request.screenId,
      },
    );
  }

  async generateStrategyBlockAssetSlot(request: {
    projectId: string;
    strategyId: string;
    blockId: string;
    slotKey: string;
    screenId: string;
    generation?: Omit<CliStrategyBlockAssetSlotGenerateRequest, 'screenId'>;
  }): Promise<CliResponseEnvelope<CliStrategyBlockAssetSlotGenerateData>> {
    return this.post(
      this.strategyBlockAssetSlotPath(request, request.slotKey, { screenId: request.screenId }, 'generate'),
      {
        ...(request.generation ?? {}),
        screenId: request.screenId,
      },
    );
  }

  // ─── Motivation layer (Simonov needs) ──────────────────────────
  // Synchronous deterministic engine (same path as the CLI `internal actors
  // motivation` / `internal strategy score`); responses carry the result inline.

  /**
   * Derive deterministic Simonov motivation profiles for actors (one actor, a whole
   * audience, or the whole project). Returns the derived records inline. Edit-wins:
   * actors whose latest stored profile is source==='manual' are returned under
   * `warnings` and skipped unless `force:true` is passed.
   */
  async deriveMotivation(request: {
    projectId: string;
    audienceId?: string;
    actorId?: string;
    persist?: boolean;
    force?: boolean;
    trust?: number;
  }): Promise<{
    success: boolean;
    error?: string;
    records?: Array<Record<string, any>>;
    warnings?: Array<{ audienceId: string; actorId: string; actorName?: string; reason: string }>;
    persisted?: boolean;
  }> {
    return this.post('/api/agent/derive-motivation', request);
  }

  /** Persist a hand-edited motivation profile as a new version (source='manual'). */
  async saveMotivationProfile(request: {
    projectId: string;
    audienceId: string;
    actorId: string;
    profile: Record<string, any>;
    note?: string;
    actorName?: string;
  }): Promise<{ success: boolean; error?: string; record?: Record<string, any> }> {
    return this.post('/api/agent/save-motivation-profile', request);
  }

  /** Score a strategy's screens against an audience's motivation profiles. */
  async scoreStrategy(request: {
    projectId: string;
    strategyId: string;
    audienceId?: string;
    persist?: boolean;
    multiplier?: number;
    goalGc?: number;
  }): Promise<{ success: boolean; error?: string; scorecard?: Record<string, any> }> {
    return this.post('/api/agent/score-strategy', request);
  }

  /**
   * Generate insights analysis with AI.
   * Fire-and-forget: returns taskId + analysisId immediately.
   * Backend loads actors, runs AI aggregation, then deterministic computations.
   * Frontend tracks progress via Firestore real-time listener on task document.
   */
  async generateInsights(request: AgentGenerateInsightsRequest): Promise<AgentGenerateInsightsResponse> {
    return this.post<AgentGenerateInsightsResponse>('/api/agent/generate-insights', request);
  }

  /**
   * Resume/retry an existing insights analysis from its frozen input snapshots.
   * Fire-and-forget: returns taskId + analysisId immediately.
   */
  async resumeInsights(request: AgentResumeInsightsRequest): Promise<AgentResumeInsightsResponse> {
    return this.post<AgentResumeInsightsResponse>('/api/agent/resume-insights', request);
  }

  /**
   * Generate variables from insights analysis with AI.
   * Fire-and-forget: returns taskId + generationId immediately.
   * Backend loads insights, runs AI variable generation, saves to subcollection.
   * Frontend tracks progress via Firestore real-time listener on task document.
   */
  async generateInsightVariables(request: AgentGenerateInsightVariablesRequest): Promise<AgentGenerateInsightVariablesResponse> {
    return this.post<AgentGenerateInsightVariablesResponse>('/api/agent/generate-insight-variables', request);
  }

  /**
   * Apply accepted insight-derived variables to project variableSet.
   * Reads accepted entities from generation subcollection, merges into project variables.
   */
  async applyInsightVariables(generationId: string, params: { projectId: string; userId: string }): Promise<{ success: boolean; appliedCount?: number; error?: string }> {
    return this.post(`/api/agent/insight-variables/${generationId}/apply`, params);
  }

  /**
   * Generate audiences with AI.
   * Fire-and-forget: returns taskId immediately.
   * Supports two modes: standard AI generation and Deep Research (Gemini Interactions API).
   * Frontend tracks progress via Firestore real-time listener on task document.
   */
  async generateAudiences(request: AgentGenerateAudiencesRequest): Promise<AgentGenerateAudiencesResponse> {
    return this.post<AgentGenerateAudiencesResponse>('/api/agent/generate-audiences', request);
  }

  /**
   * Generate Theme V2 analysis/projection from one or more image references.
   * Fire-and-forget: returns taskId immediately, backend writes the completed
   * analysis/projection to backend_tasks/{taskId}.result.
   */
  async generateThemeV2FromImage(
    request: AgentGenerateThemeV2FromImageRequest
  ): Promise<AgentGenerateThemeV2FromImageResponse> {
    return this.post<AgentGenerateThemeV2FromImageResponse>('/api/agent/theme-v2/generate-from-image', request);
  }

  /**
   * Analyze screenshot references for screen reproduction (Stages 0–2:
   * CV geometry, VLM semantics, deterministic routing). Executed in-request:
   * the response carries the completed analysis plus the backend task id.
   */
  async analyzeScreenReproduction(
    request: AgentAnalyzeScreenReproductionRequest
  ): Promise<AgentAnalyzeScreenReproductionResponse> {
    return this.post<AgentAnalyzeScreenReproductionResponse>('/api/agent/screen-reproduction/analyze', request);
  }

  /**
   * Generate screen reproduction candidates from a routed analysis (Stages 3–4:
   * generation + render-and-compare). Executed in-request: the response carries
   * the persisted reproduction result plus the backend task id. Artifact-first:
   * nothing is auto-applied — the manifest is applied via `funnels apply`.
   */
  async generateScreenReproduction(
    request: AgentGenerateScreenReproductionRequest
  ): Promise<AgentGenerateScreenReproductionResponse> {
    return this.post<AgentGenerateScreenReproductionResponse>('/api/agent/screen-reproduction/generate', request);
  }

  /**
   * Generate an image using AI (Google Gemini or OpenAI gpt-image-*).
   * Fire-and-forget: returns taskId immediately. Result written to Firestore
   * screen/post document via aiAssets field (real-time listener picks up).
   *
   * Provider routing: when `provider` is omitted, the backend infers it from
   * the `model` prefix (`gemini-*` → google, `gpt-image-*` → openai).
   */
  async generateImage(params: {
    projectId: string;
    strategyId?: string;
    blockId?: string;
    screenId: string;
    slotKey: string;
    experimentId?: string;
    prompt: string;
    negativePrompt?: string;
    aspectRatio?: string;
    /** Gemini-only resolution: '512' | '1K' | '2K' | '4K'. */
    imageSize?: string;
    /** OpenAI-only explicit size: 'auto' | '1024x1024' | '1024x1536' | etc. */
    openaiSize?: string;
    /** OpenAI-only quality tier. */
    quality?: 'low' | 'medium' | 'high' | 'auto';
    style?: string;
    model?: string;
    /** Provider override. Inferred from `model` prefix when omitted. */
    provider?: 'google' | 'openai';
    referenceImages?: Array<{
      url: string;
      mimeType: string;
      role?: string;
      name?: string;
      instruction?: string;
    }>;
    userId: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/generate-image', params);
  }

  // ─── Scrape & Analyze ──────────────────────────────────────────

  /**
   * Scrape URLs and analyze with AI.
   * Fire-and-forget: returns taskId + analysisId immediately.
   * Backend scrapes URLs, runs AI analysis per goal, saves results.
   * Two modes: 'auto' (direct save) or 'review' (staging for user approval).
   */
  async scrapeAnalyze(request: AgentScrapeAnalyzeRequest): Promise<AgentScrapeAnalyzeResponse> {
    return this.post<AgentScrapeAnalyzeResponse>('/api/agent/scrape-analyze', request);
  }

  /**
   * Apply accepted staged entities to final Firestore collections.
   * Used after user reviews staged entities in 'review' mode.
   */
  async applyStagedEntities(
    analysisId: string,
    request: AgentApplyStagedRequest,
  ): Promise<AgentApplyStagedResponse> {
    return this.post<AgentApplyStagedResponse>(
      `/api/agent/scrape-analyze/${analysisId}/apply`,
      request,
    );
  }

  /**
   * Re-analyze existing scrape results with different goals.
   * Skips scraping phase, uses stored raw content.
   */
  async rerunAnalysis(request: AgentScrapeAnalyzeRequest): Promise<AgentScrapeAnalyzeResponse> {
    return this.post<AgentScrapeAnalyzeResponse>('/api/agent/scrape-analyze/rerun', request);
  }

  // ─── Actor Agents ──────────────────────────────────────────────

  /**
   * Adapt generic JTBD interview questions to product context.
   * Fire-and-forget: returns taskId immediately.
   * Result saved to project's `adoptedQuestions` field.
   */
  async adaptQuestions(request: {
    projectId: string;
    userId: string;
    provider?: string;
    model?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/adapt-questions', request);
  }

  /**
   * Generate synthetic actor personas for audiences.
   * Fire-and-forget: returns taskId immediately.
   * Actors saved to Firestore subcollections.
   */
  async generateActors(request: {
    projectId: string;
    userId: string;
    audiences: Array<{
      audienceId: string;
      audienceName: string;
      audienceDescription: string;
      actorCount: number;
      acquisitionChannels?: Array<{
        channel: string;
        label: string;
        percentage: number;
        fit: 'primary' | 'secondary' | 'supporting';
        rationale: string;
      }>;
    }>;
    provider?: string;
    model?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/generate-actors', request);
  }

  /**
   * Conduct AI interviews with selected actors and extract JTBD insights.
   * Fire-and-forget: returns taskId immediately.
   * Two-phase pipeline: interview simulation → JTBD analysis.
   * Results saved to actor documents and 7 JTBD subcollections.
   */
  async conductInterview(request: {
    projectId: string;
    userId: string;
    actors: Array<{
      actorId: string;
      audienceId: string;
      actorName: string;
      actorDescription: string;
      audienceName: string;
      taskId?: string;
      taskName?: string;
      taskDescription?: string;
      acquisitionChannels?: Array<{
        channel: string;
        label: string;
        percentage: number;
        fit: 'primary' | 'secondary' | 'supporting';
        rationale: string;
      }>;
    }>;
    provider?: string;
    model?: string;
  }): Promise<{ success: boolean; taskId?: string; error?: string }> {
    return this.post('/api/agent/conduct-interview', request);
  }

  /**
   * Cancel an in-progress request.
   */
  cancel(): void {
    if (this.abortController) {
      this.abortController.abort();
    }
  }

  // ─── Private ─────────────────────────────────────────────────

  private async get<T>(path: string): Promise<T> {
    return this.request<T>('GET', path);
  }

  private async patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('PATCH', path, body);
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>('POST', path, body);
  }

  private async request<T>(method: 'GET' | 'PATCH' | 'POST', path: string, body?: unknown): Promise<T> {
    this.abortController = new AbortController();

    try {
      const token = await getAuth().currentUser?.getIdToken();
      if (!token) {
        return { success: false, error: 'User not authenticated' } as T;
      }
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
        signal: this.abortController.signal,
      });

      const contentType = response.headers.get('content-type') || '';
      const isJsonResponse = contentType.toLowerCase().includes('application/json');
      const data = isJsonResponse ? await response.json() : await response.text();

      if (!response.ok) {
        const blocked = isJsonResponse
          ? parseBillingBlockedPayload(response.status, data as Record<string, unknown>)
          : null;
        if (blocked) {
          const projectId = typeof (body as any)?.projectId === 'string' ? (body as any).projectId : undefined;
          emitBillingBlocked(blocked.billingBlock, { projectId, actionLabel: path });
          /**
           * Final review Important #1 (FW9-R4): a 402 billing block is a
           * SYNCHRONOUS, certain refusal like every other 4xx — the server
           * started nothing — so it carries `httpStatus` too and a caller that
           * must compensate (`StrategyOrchestrator.createWithAIServer`) undoes
           * its create instead of leaving an orphan head. The billing block
           * travels on as before: the global dialog still opens.
           */
          return agentApiRefusal({
            status: response.status,
            error: blocked.message,
            billingBlock: blocked.billingBlock,
          }) as T;
        }
        const apiError = isJsonResponse
          ? (data as { error?: string }).error
          : this.formatNonJsonRouteError(path, response.status);
        console.error(`[AgentApiClient] ${path} error:`, apiError);
        /**
         * F-LIVE2-40: a caller that must compensate needs to know the refusal was
         * SYNCHRONOUS and certain (a 4xx validation answer) rather than a 5xx /
         * network failure whose server-side effect is unknown. The field belongs
         * to the shared error shape every endpoint's failure branch returns, so
         * it is declared rather than smuggled in through the cast (Minor #8) —
         * and built by the SAME constructor as the billing branch above, so no
         * HTTP refusal can be shaped without it (final review Important #1).
         */
        const refusal: AgentApiErrorResponse = agentApiRefusal({
          status: response.status,
          error: apiError || `API error: ${response.status}`,
        });
        return refusal as T;
      }

      if (!isJsonResponse) {
        return {
          success: false,
          error: this.formatNonJsonRouteError(path, response.status),
        } as T;
      }

      return data as T;
    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: false, error: 'Request cancelled' } as T;
      }

      console.error(`[AgentApiClient] ${path} network error:`, error);
      return { success: false, error: error.message || 'Network error' } as T;
    } finally {
      this.abortController = null;
    }
  }

  private strategyBlockAssetPath(
    request: { projectId: string; strategyId: string; blockId: string },
    action?: string,
  ): string {
    return [
      '/api/cli/v1/projects',
      encodeURIComponent(request.projectId),
      'strategies',
      encodeURIComponent(request.strategyId),
      'blocks',
      encodeURIComponent(request.blockId),
      'assets',
      ...(action ? [encodeURIComponent(action)] : []),
    ].join('/');
  }

  private strategyBlockAssetSlotPath(
    request: { projectId: string; strategyId: string; blockId: string },
    slotKey?: string,
    query?: { screenId?: string | null },
    action?: string,
  ): string {
    const base = [
      this.strategyBlockAssetPath(request),
      'slots',
      ...(slotKey ? [encodeURIComponent(slotKey), ...(action ? [encodeURIComponent(action)] : [])] : []),
    ].join('/');
    const params = new URLSearchParams();
    if (query?.screenId) {
      params.set('screenId', query.screenId);
    }
    const search = params.toString();
    return search ? `${base}?${search}` : base;
  }

  private formatNonJsonRouteError(path: string, status: number): string {
    const localPage = typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const cloudApi = this.baseUrl.includes('dev-api.segmently.ai') || this.baseUrl.includes('api.segmently.ai');
    const localHint = localPage && cloudApi
      ? ' Local dev is currently pointing at the cloud API; switch VITE_AGENT_API_URL/VITE_BUILDER_API_URL to local builder or run the deployed backend that contains this route.'
      : '';
    return `API route returned non-JSON ${status} from ${this.baseUrl}${path}. The route is likely not deployed or not registered.${localHint}`;
  }
}

/**
 * Get agent execution mode.
 * Always returns 'server' — all AI calls go through builder-service.
 * @deprecated Will be removed. Agent mode is always 'server'.
 */
export function getAgentMode(): 'server' {
  return 'server';
}

export default AgentApiClient;
